import type { OfferItem } from '../../model/pages';
import type { SectionKit } from './kit';

/** Offer box: what is included (each line with an optional "value" note), the
 *  total value struck through, today's price, one full-width button and a
 *  reassurance line. An optional product image sits on top of the card. */
export default function OfferBlock({ k }: { k: SectionKit }) {
  const { section, s, brand, onBrand, btnBg, onBtn, tz, txt, hl, link, eyebrowCls, ph, editable } = k;
  const all = section.offer_items ?? [];
  const rows = all.map((o, i) => ({ o, i })).filter(({ o }) => Boolean(o.label));
  if (!rows.length && !section.price && !section.heading) return ph('Offer', 'Add what is included and the price in the panel');
  const patch = (i: number, p: Partial<OfferItem>) => { const a = [...all]; a[i] = { ...a[i], ...p }; return { offer_items: a }; };
  return (
    <div className={`mx-auto w-full max-w-2xl overflow-hidden ${s.card}`} style={k.contentStyle}>
      {section.image_url && (
        <div className={`border-b px-6 pt-6 sm:px-10 sm:pt-8 ${s.divider}`}>
          <img src={section.image_url} alt="" loading="lazy" decoding="async" className="mx-auto max-h-72 w-auto object-contain" />
        </div>
      )}
      <div className="grid gap-6 p-6 sm:p-10">
        {(section.eyebrow || section.heading) && (
          <div className="grid gap-2 text-center">
            {section.eyebrow && <span className={`mx-auto ${tz('eyebrow')} ${eyebrowCls}`}>{txt(section.eyebrow, (t) => ({ eyebrow: t }))}</span>}
            {section.heading && <h2 className={`${tz('heading')} ${s.heading}`}>{hl(section.heading, (t) => ({ heading: t }))}</h2>}
            {section.subheadline && <p className={`${tz('small')} ${s.sub}`}>{txt(section.subheadline, (t) => ({ subheadline: t }), true)}</p>}
          </div>
        )}
        {rows.length > 0 && (
          <ul className={`grid divide-y ${s.divider}`}>
            {rows.map(({ o, i }) => (
              <li key={i} className={`flex items-start justify-between gap-4 py-3 first:pt-0 ${s.divider}`}>
                <span className={`flex min-w-0 items-start gap-3 ${tz('body')}`}>
                  <span aria-hidden className="mt-[3px] grid h-5 w-5 flex-none place-items-center rounded-full" style={{ background: brand, color: onBrand }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                  </span>
                  <span className="font-medium">{txt(o.label, (t) => patch(i, { label: t }))}</span>
                </span>
                {o.value && <span className={`shrink-0 whitespace-nowrap pt-[2px] text-sm ${s.label ?? s.sub}`}>{txt(o.value, (t) => patch(i, { value: t }))}</span>}
              </li>
            ))}
          </ul>
        )}
        {(section.price || section.price_compare) && (
          <div className="grid justify-items-center gap-1 text-center">
            {section.price_compare && (
              <span className={`text-sm ${s.label ?? s.sub}`}>Total value <s className="decoration-2">{txt(section.price_compare, (t) => ({ price_compare: t }))}</s></span>
            )}
            {section.price && (
              <span className={`text-5xl font-extrabold leading-none tracking-tight sm:text-6xl ${s.heading}`}>{txt(section.price, (t) => ({ price: t }))}</span>
            )}
            {section.price_note && <span className={`mt-1 text-sm ${s.sub}`}>{txt(section.price_note, (t) => ({ price_note: t }))}</span>}
          </div>
        )}
        {/* Editor canvas: a missing button label shows a "Button text" placeholder. */}
        {(section.label || editable) && (
          <div className="grid gap-3">
            {link(section.label ?? '', section.href || '#form', `grid w-full place-items-center px-6 py-4 text-base sm:text-lg ${s.button}`, { background: btnBg, color: onBtn },
              txt(section.label ?? '', (t) => ({ label: t })))}
            {section.cta_note && (
              <span className={`flex items-center justify-center gap-1.5 text-center text-xs ${s.label ?? s.sub}`}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><rect x="4.5" y="10.5" width="15" height="10" rx="2" /><path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" /></svg>
                {txt(section.cta_note, (t) => ({ cta_note: t }))}
              </span>
            )}
          </div>
        )}
        {section.body && <p className={`text-center text-xs leading-relaxed ${s.label ?? s.sub} whitespace-pre-line`}>{txt(section.body, (t) => ({ body: t }), true)}</p>}
      </div>
    </div>
  );
}
